import { useState } from 'react';
import { useLogin, useNotify } from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    InputAdornment,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import { colors } from '../theme';

export const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const login = useLogin();
    const notify = useNotify();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login({ username, password });
        } catch (error) {
            notify(
                error instanceof Error ? error.message : 'Invalid credentials',
                { type: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${colors.slate[900]} 0%, ${colors.slate[950]} 100%)`,
                p: 2,
            }}
        >
            <Card
                sx={{
                    width: '100%',
                    maxWidth: 400,
                    borderRadius: '20px',
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    {/* Logo */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 4,
                        }}
                    >
                        <Box
                            component="img"
                            src="/images/xtconnect-logo.png"
                            alt="XTConnect"
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '16px',
                                objectFit: 'cover',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                            }}
                        />
                    </Box>

                    {/* Title */}
                    <Typography
                        variant="h4"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 700,
                            color: colors.slate[900],
                            mb: 1,
                        }}
                    >
                        XTConnect
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: colors.slate[500],
                            mb: 4,
                        }}
                    >
                        Sign in to Admin Console
                    </Typography>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        <TextField
                            name="username"
                            label="Email"
                            type="email"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            fullWidth
                            required
                            autoFocus
                            sx={{ mb: 2 }}
                            InputProps={{
                                sx: { borderRadius: '12px' },
                            }}
                        />

                        <TextField
                            name="password"
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                            required
                            sx={{ mb: 3 }}
                            InputProps={{
                                sx: { borderRadius: '12px' },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? (
                                                <VisibilityOffIcon />
                                            ) : (
                                                <VisibilityIcon />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                borderRadius: '12px',
                                backgroundImage: `linear-gradient(135deg, ${colors.primary}, ${colors.tertiary})`,
                                boxShadow: `0 4px 12px ${alpha(colors.primary, 0.4)}`,
                                fontSize: '1rem',
                                fontWeight: 600,
                                '&:hover': {
                                    backgroundImage: `linear-gradient(135deg, ${colors.primary}, ${colors.tertiary})`,
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 6px 16px ${alpha(colors.primary, 0.5)}`,
                                },
                                '&:disabled': {
                                    backgroundImage: 'none',
                                },
                            }}
                            startIcon={
                                loading ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    <LockIcon />
                                )
                            }
                        >
                            {loading ? 'Signing in...' : 'Sign In'}
                        </Button>
                    </form>

                    {/* Footer */}
                    <Typography
                        variant="body2"
                        sx={{
                            textAlign: 'center',
                            color: colors.slate[400],
                            mt: 4,
                            fontSize: 12,
                        }}
                    >
                        XTConnect Administration Console
                    </Typography>
                </CardContent>
            </Card>
        </Box>
    );
};

export default LoginPage;
